import { create } from 'zustand';
import {
  remapAudienceSegmentValues,
  type CompTopicDetailBucket,
  type FilterConnector,
  type FilterGroup,
  type FilterOperator,
  type FilterRule,
  type OverviewFilters,
} from '@nexus/contracts';
import { migrateLegacyKpiId } from '@nexus/kpi-formula';

// ─── State shape ──────────────────────────────────────────────────────────────

/** My-Brand OverviewChart metric selection — display-only (never hits the network/hash). */
export interface ChartMetricSelection {
  /** Stable catalog id (see metricCatalog.ts). */
  id: string;
  /** Per-metric sort; the active sort metric's direction orders the topics. */
  sort: 'asc' | 'desc';
  /** Which pinned axis pane the KPI reads against — see LOCKED_CHART_METRIC_ID. */
  axis: 'left' | 'right';
}

export type ChartViewMode = 'bars' | 'table';

const CHART_VIEW_MODES: readonly ChartViewMode[] = ['bars', 'table'];

/**
 * Hard cap on simultaneously-selected chart metrics (legibility). Three is the
 * point where a category band still fits one bar per metric WITH its value
 * label across a full viewport of concepts.
 */
export const MAX_CHART_METRICS = 3;

/**
 * Spend anchors the chart: it is always selected and always the axis pinned to the
 * left of the plot. Every other KPI is optional and reads against a pinned axis on
 * the right. Topic order follows `chartSortMetricId`, which defaults to Spend.
 */
export const LOCKED_CHART_METRIC_ID = 'Spend';

export interface OverviewFilterBarState extends Omit<OverviewFilters, 'startDate' | 'endDate'> {
  /**
   * My-Brand ad status. Kept separate from the inherited `effectiveStatus` (which is the
   * Competitors selection) because the two modes read different BQ tables with different
   * status domains — see `@/lib/ad-status`. A single shared field would leak a Competitors
   * value like `inactive` into My Brand, where it matches nothing for most tenants.
   * `OverviewPage` maps whichever field matches the active mode onto the wire param.
   */
  myBrandEffectiveStatus: string[];
  /** Display-only breakdown dimension — never crosses the network or hash. */
  breakdown: string;
  /** Competitors-mode display-only Performance Metric pill — which dots plot, not topic order. */
  performanceMetric: string;
  /** My-Brand OverviewChart selected metrics (index 0 = Spend, left axis). Display-only. */
  chartMetrics: ChartMetricSelection[];
  /** Which selected KPI currently orders the topics. Display-only. */
  chartSortMetricId: string;
  /** My-Brand OverviewChart active visualization. Display-only. */
  chartViewMode: ChartViewMode;
  /**
   * Competitors chart legend sort. `null` keeps summed-Assets desc.
   * Display-only — never crosses the network, hash, or Apply/Reset dirty.
   */
  competitorChartSortBucket: CompTopicDetailBucket | null;
  competitorChartSortDirection: 'asc' | 'desc';
}

const DEFAULT_STATE: OverviewFilterBarState = {
  groupBy: '',
  groupByValues: [],
  subFeature: ['Main'],
  featureTimeFrame: [],
  objectType: [],
  effectiveStatus: [],
  myBrandEffectiveStatus: [],
  audienceSegment: [],
  device: [],
  scenario: ['Concept'],
  advancedFilters: [],
  breakdown: 'Default',
  performanceMetric: 'Days/Assets',
  chartMetrics: [
    { id: 'Spend', sort: 'desc', axis: 'left' },
    { id: 'CPA', sort: 'desc', axis: 'right' },
  ],
  chartSortMetricId: LOCKED_CHART_METRIC_ID,
  chartViewMode: 'bars',
  competitorChartSortBucket: null,
  competitorChartSortDirection: 'desc',
};

/**
 * The committable filter fields. Chart display fields are deliberately absent:
 * they are not filters, so editing them must neither light up the global
 * "Apply changes" button nor be reverted by Reset/Discard.
 */
export type OverviewAppliedState = Omit<
  OverviewFilterBarState,
  | 'chartMetrics'
  | 'chartViewMode'
  | 'chartSortMetricId'
  | 'competitorChartSortBucket'
  | 'competitorChartSortDirection'
>;

/** Snapshot the draft filter fields into a standalone applied-state object. */
function snapshotDraft(state: OverviewFilterBarState): OverviewAppliedState {
  return {
    groupBy: state.groupBy,
    groupByValues: state.groupByValues,
    subFeature: state.subFeature,
    featureTimeFrame: state.featureTimeFrame,
    objectType: state.objectType,
    effectiveStatus: state.effectiveStatus,
    myBrandEffectiveStatus: state.myBrandEffectiveStatus,
    audienceSegment: state.audienceSegment,
    device: state.device,
    scenario: state.scenario,
    advancedFilters: state.advancedFilters,
    breakdown: state.breakdown,
    performanceMetric: state.performanceMetric,
  };
}

// ─── Advanced-filter factories (mirror the drilldown store) ─────────────────────

function makeDefaultRule(field: string, operator: FilterOperator = 'is'): FilterRule {
  return { connector: 'And', field, operator, value: '' };
}

function makeDefaultGroup(field: string, operator: FilterOperator = 'is', connector: FilterConnector = 'And'): FilterGroup {
  return { connector, rules: [makeDefaultRule(field, operator)] };
}

// ─── localStorage ─────────────────────────────────────────────────────────────

const STORAGE_KEY = 'nexus_overview_filters';
// v2: effectiveStatus values switched from UPPERCASE to lowercase.
// v3: added draft `advancedFilters` + `appliedAdvancedFilters`.
// v4: every filter is now draft-only and commits together via the global "Apply changes"
//     button — the single `appliedAdvancedFilters` field is replaced by a full `applied`
//     snapshot. The version gate in readStorage() discards older shapes so they can't rehydrate.
// v5: added My-Brand OverviewChart display fields `chartMetrics` + `chartViewMode`.
// v6: added `groupByValues` (dimension-specific Group By value sub-filter). The chart display
//     fields later left the `applied` snapshot; rather than bump (which discards every saved
//     filter selection), readStorage() re-snapshots `applied` so v6 blobs load clean.
//     `myBrandEffectiveStatus` was added later without a bump for the same reason: a v6 blob
//     simply lacks the key, so DEFAULT_STATE supplies `[]` and the `applied` re-snapshot below
//     fills it. `device` was added the same way: a missing key is All. The pre-existing `effectiveStatus` needs no sanitizing — it only ever held the
//     Competitors vocabulary (`active` / `inactive`), which is exactly what it still means.
// v7: chart metric ids migrated from camelCase catalog ids to canonical `Kpi.name`.
const STORAGE_VERSION = 7;
const LEGACY_STORAGE_VERSION = 6;

interface PersistedState extends OverviewFilterBarState {
  version: number;
  _lastResetTenantId: string | null;
  applied: OverviewAppliedState;
}

/**
 * Enforce the chart's invariants on a selection from any source: Spend present, first,
 * and bound to the left axis; every other KPI on the right; no more than the cap. A
 * selection saved before Spend was locked (or while the cap was higher) would otherwise
 * rehydrate straight past rules that are only applied when adding.
 */
function normalizeChartMetrics(metrics: ChartMetricSelection[]): ChartMetricSelection[] {
  const migrated = metrics.map((m) => ({ ...m, id: migrateLegacyKpiId(m.id) }));
  const locked = migrated.find((m) => m.id === LOCKED_CHART_METRIC_ID);
  const rest = migrated.filter((m) => m.id !== LOCKED_CHART_METRIC_ID);
  return [
    { ...(locked ?? { id: LOCKED_CHART_METRIC_ID, sort: 'desc' as const }), axis: 'left' as const },
    ...rest.map((m) => ({ ...m, axis: 'right' as const })),
  ].slice(0, MAX_CHART_METRICS);
}

/** Falls back to `bars` for retired modes (e.g. the removed `line` view) so a stale
 *  persisted value can't leave the view-mode toggle with nothing selected. */
function coerceChartViewMode(mode: ChartViewMode | undefined): ChartViewMode {
  return mode && CHART_VIEW_MODES.includes(mode) ? mode : 'bars';
}

/** Fall back to Spend when the id is missing or no longer among the selected KPIs. */
function coerceChartSortMetricId(
  sortMetricId: string | undefined,
  metrics: ChartMetricSelection[],
): string {
  if (sortMetricId && metrics.some((m) => m.id === sortMetricId)) return sortMetricId;
  return LOCKED_CHART_METRIC_ID;
}

function coerceCompetitorChartSortBucket(value: unknown): CompTopicDetailBucket | null {
  if (value === 'you' || value === 'mainCompetitor' || value === 'benchmark') return value;
  return null;
}

function coerceCompetitorChartSortDirection(value: unknown): 'asc' | 'desc' {
  return value === 'asc' ? 'asc' : 'desc';
}

function readStorage(): Partial<PersistedState> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Partial<PersistedState>;
    if (parsed.version !== STORAGE_VERSION && parsed.version !== LEGACY_STORAGE_VERSION) return {};
    if (parsed.chartMetrics) parsed.chartMetrics = normalizeChartMetrics(parsed.chartMetrics);
    if (parsed.chartViewMode) parsed.chartViewMode = coerceChartViewMode(parsed.chartViewMode);
    // After normalize — a sliced-out KPI must not remain the active sort.
    parsed.chartSortMetricId = coerceChartSortMetricId(
      parsed.chartSortMetricId,
      parsed.chartMetrics ?? DEFAULT_STATE.chartMetrics,
    );
    parsed.competitorChartSortBucket = coerceCompetitorChartSortBucket(parsed.competitorChartSortBucket);
    parsed.competitorChartSortDirection = coerceCompetitorChartSortDirection(parsed.competitorChartSortDirection);
    // Blobs written before the chart fields left `applied` still carry them. Re-snapshotting
    // drops the strays — left in place, they would never match a draft snapshot and the
    // "Apply changes" button would load stuck on.
    if (parsed.applied) {
      parsed.applied = snapshotDraft({ ...DEFAULT_STATE, ...parsed.applied });
    }
    if (parsed.audienceSegment) parsed.audienceSegment = remapAudienceSegmentValues(parsed.audienceSegment);
    if (parsed.applied?.audienceSegment) {
      parsed.applied = { ...parsed.applied, audienceSegment: remapAudienceSegmentValues(parsed.applied.audienceSegment) };
    }
    return parsed;
  } catch {
    return {};
  }
}

function writeStorage(state: PersistedState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch { /* incognito / quota exceeded */ }
}

// ─── Store interface ──────────────────────────────────────────────────────────

interface OverviewFiltersStore extends OverviewFilterBarState {
  /** Tracks the last tenant for which filters were reset — prevents re-reset on page navigation. */
  _lastResetTenantId: string | null;
  /** Committed filters — only these drive the summary query. Draft edits wait for `applyFilters`. */
  applied: OverviewAppliedState;
  setFilter: <K extends keyof OverviewFilterBarState>(key: K, value: OverviewFilterBarState[K]) => void;
  resetFilters: () => void;
  resetForTenant: (tenantId: string | null) => void;
  // ── Advanced filters (draft edits; commit via applyFilters / revert via discardFilters) ──
  addFilterGroup: (defaultField: string, defaultOperator?: FilterOperator) => void;
  removeFilterGroup: (groupIndex: number) => void;
  setGroupConnector: (groupIndex: number, connector: FilterConnector) => void;
  addFilterRule: (groupIndex: number, defaultField: string, defaultOperator?: FilterOperator) => void;
  removeFilterRule: (groupIndex: number, ruleIndex: number) => void;
  updateFilterRule: (groupIndex: number, ruleIndex: number, patch: Partial<FilterRule>) => void;
  /** Commit all draft fields into `applied` — drives the summary query refetch. */
  applyFilters: () => void;
  /** Revert all draft fields back to the last applied snapshot. */
  discardFilters: () => void;
  /** Replace draft + applied snapshot atomically (e.g. when applying a saved preset). */
  loadPresetState: (snapshot: OverviewAppliedState) => void;
  /** Drop a device selection from draft and applied without committing other drafts. */
  clearDevice: () => void;
  // ── My-Brand OverviewChart metric selection (display-only; no query impact) ──
  /** Append a metric on the right axis. No-op if selected or at MAX_CHART_METRICS. */
  addChartMetric: (id: string) => void;
  /** Remove a metric — no-op for the locked Spend metric. */
  removeChartMetric: (id: string) => void;
  /** Promote a metric to the active sort, or flip its direction if already active. */
  toggleChartMetricSort: (id: string) => void;
  /** Promote a Competitors-chart series to the active sort, or flip its direction if already active. */
  toggleCompetitorChartSort: (bucket: CompTopicDetailBucket) => void;
  /** Switch the active visualization (bars/table). */
  setChartViewMode: (mode: ChartViewMode) => void;
}

/**
 * True when any draft filter field diverges from the committed `applied` snapshot.
 *
 * Compares field by field over the snapshot's own keys rather than stringifying the two
 * objects whole. TypeScript does not flag a wide object spread into a narrowed slot, so a
 * single stray `applied: { ...DEFAULT_STATE }` would otherwise smuggle the chart fields
 * back in and leave this permanently true, with nothing failing to compile. Per-key also
 * makes the result independent of key order.
 */
export function isOverviewDirty(state: OverviewFiltersStore): boolean {
  const draft = snapshotDraft(state);
  return (Object.keys(draft) as (keyof OverviewAppliedState)[]).some(
    (key) => JSON.stringify(draft[key]) !== JSON.stringify(state.applied[key]),
  );
}

/** Stable key of the committable draft fields — used to detect "any filter changed". */
export const selectOverviewDraftKey = (state: OverviewFilterBarState): string =>
  JSON.stringify(snapshotDraft(state));

/** Stable key for a preset's page filters — mirrors live draft key after loadPresetState. */
export const overviewAppliedKey = (applied: OverviewAppliedState): string =>
  JSON.stringify(snapshotDraft({ ...DEFAULT_STATE, ...applied }));

function persist(next: OverviewFiltersStore): void {
  writeStorage({
    version: STORAGE_VERSION,
    groupBy: next.groupBy,
    groupByValues: next.groupByValues,
    subFeature: next.subFeature,
    featureTimeFrame: next.featureTimeFrame,
    objectType: next.objectType,
    effectiveStatus: next.effectiveStatus,
    myBrandEffectiveStatus: next.myBrandEffectiveStatus,
    audienceSegment: next.audienceSegment,
    device: next.device,
    scenario: next.scenario,
    advancedFilters: next.advancedFilters,
    breakdown: next.breakdown,
    performanceMetric: next.performanceMetric,
    chartMetrics: next.chartMetrics,
    chartSortMetricId: next.chartSortMetricId,
    chartViewMode: next.chartViewMode,
    competitorChartSortBucket: next.competitorChartSortBucket,
    competitorChartSortDirection: next.competitorChartSortDirection,
    applied: next.applied,
    _lastResetTenantId: next._lastResetTenantId,
  });
}

// ─── Create store ─────────────────────────────────────────────────────────────

const saved = readStorage();
const initialChartMetrics = normalizeChartMetrics(saved.chartMetrics ?? DEFAULT_STATE.chartMetrics);
const initial: Pick<OverviewFiltersStore, keyof OverviewFilterBarState | '_lastResetTenantId' | 'applied'> = {
  ...DEFAULT_STATE,
  ...saved,
  chartMetrics: initialChartMetrics,
  chartSortMetricId: coerceChartSortMetricId(saved.chartSortMetricId, initialChartMetrics),
  competitorChartSortBucket: coerceCompetitorChartSortBucket(saved.competitorChartSortBucket),
  competitorChartSortDirection: coerceCompetitorChartSortDirection(saved.competitorChartSortDirection),
  applied: saved.applied ?? snapshotDraft(DEFAULT_STATE),
  _lastResetTenantId: saved._lastResetTenantId ?? null,
};

export const useOverviewFiltersStore = create<OverviewFiltersStore>((set) => ({
  ...initial,

  setFilter: (key, value) =>
    set((state) => {
      const next = { ...state, [key]: value };
      if (key === 'groupBy') {
        // Group By value sub-options are dimension-specific — a value picked under one
        // dimension (e.g. Age's "18-24") doesn't exist under another and would zero the
        // filter. Clear the selection whenever the group-by dimension changes.
        next.groupByValues = [];
      }
      if (key === 'scenario') {
        // Sub-categories (subFeature) are scenario-specific — a value selected under one
        // scenario (e.g. Concept's "Main") doesn't exist under another and would zero the
        // join. Clear it whenever the scenario selection changes.
        next.subFeature = [];
        // NOTE: Section (featureTimeFrame) is intentionally NOT touched here. Metadata
        // scenarios (Brand/Asset Metadata) are section-agnostic in the backend query
        // (overview.sql.ts exempts them from the Section filter), so selecting one no longer
        // needs to force Section to "None" — Section persists independently, matching Retool.
      }
      persist(next);
      return next;
    }),

  // Reset/resetForTenant restore the filter fields only — the chart's KPI selection and
  // view mode are display state, so clearing filters (or switching tenant) leaves them be.
  resetFilters: () =>
    set((state) => {
      const next = { ...state, ...snapshotDraft(DEFAULT_STATE), applied: snapshotDraft(DEFAULT_STATE) };
      persist(next);
      return next;
    }),

  resetForTenant: (tenantId) =>
    set((state) => {
      if (state._lastResetTenantId === tenantId) return {};
      const next: OverviewFiltersStore = { ...state, ...snapshotDraft(DEFAULT_STATE), applied: snapshotDraft(DEFAULT_STATE), _lastResetTenantId: tenantId };
      persist(next);
      return next;
    }),

  addFilterGroup: (defaultField, defaultOperator = 'is') =>
    set((state) => {
      const next = { ...state, advancedFilters: [...state.advancedFilters, makeDefaultGroup(defaultField, defaultOperator)] };
      persist(next);
      return next;
    }),

  removeFilterGroup: (groupIndex) =>
    set((state) => {
      const next = { ...state, advancedFilters: state.advancedFilters.filter((_, i) => i !== groupIndex) };
      persist(next);
      return next;
    }),

  setGroupConnector: (groupIndex, connector) =>
    set((state) => {
      const advancedFilters = state.advancedFilters.map((g, i) => (i === groupIndex ? { ...g, connector } : g));
      const next = { ...state, advancedFilters };
      persist(next);
      return next;
    }),

  addFilterRule: (groupIndex, defaultField, defaultOperator = 'is') =>
    set((state) => {
      const advancedFilters = state.advancedFilters.map((g, i) =>
        i === groupIndex ? { ...g, rules: [...g.rules, makeDefaultRule(defaultField, defaultOperator)] } : g,
      );
      const next = { ...state, advancedFilters };
      persist(next);
      return next;
    }),

  removeFilterRule: (groupIndex, ruleIndex) =>
    set((state) => {
      const group = state.advancedFilters[groupIndex];
      if (!group) return {};
      const newRules = group.rules.filter((_, i) => i !== ruleIndex);
      const advancedFilters = newRules.length === 0
        ? state.advancedFilters.filter((_, i) => i !== groupIndex)
        : state.advancedFilters.map((g, i) => (i === groupIndex ? { ...g, rules: newRules } : g));
      const next = { ...state, advancedFilters };
      persist(next);
      return next;
    }),

  updateFilterRule: (groupIndex, ruleIndex, patch) =>
    set((state) => {
      const advancedFilters = state.advancedFilters.map((g, i) => {
        if (i !== groupIndex) return g;
        return { ...g, rules: g.rules.map((r, j) => (j === ruleIndex ? { ...r, ...patch } : r)) };
      });
      const next = { ...state, advancedFilters };
      persist(next);
      return next;
    }),

  addChartMetric: (id) =>
    set((state) => {
      if (state.chartMetrics.length >= MAX_CHART_METRICS) return {};
      if (state.chartMetrics.some((m) => m.id === id)) return {};
      const next = {
        ...state,
        chartMetrics: [...state.chartMetrics, { id, sort: 'desc' as const, axis: 'right' as const }],
      };
      persist(next);
      return next;
    }),

  removeChartMetric: (id) =>
    set((state) => {
      if (id === LOCKED_CHART_METRIC_ID) return {};
      const chartMetrics = state.chartMetrics.filter((m) => m.id !== id);
      if (chartMetrics.length === state.chartMetrics.length) return {};
      const chartSortMetricId =
        state.chartSortMetricId === id ? LOCKED_CHART_METRIC_ID : state.chartSortMetricId;
      const next = { ...state, chartMetrics, chartSortMetricId };
      persist(next);
      return next;
    }),

  toggleChartMetricSort: (id) =>
    set((state) => {
      if (!state.chartMetrics.some((m) => m.id === id)) return {};
      if (state.chartSortMetricId !== id) {
        const chartMetrics = state.chartMetrics.map((m) =>
          m.id === id ? { ...m, sort: 'desc' as const } : m,
        );
        const next = { ...state, chartMetrics, chartSortMetricId: id };
        persist(next);
        return next;
      }
      const chartMetrics = state.chartMetrics.map((m) =>
        m.id === id ? { ...m, sort: m.sort === 'desc' ? ('asc' as const) : ('desc' as const) } : m,
      );
      const next = { ...state, chartMetrics };
      persist(next);
      return next;
    }),

  toggleCompetitorChartSort: (bucket) =>
    set((state) => {
      if (state.competitorChartSortBucket !== bucket) {
        const next = {
          ...state,
          competitorChartSortBucket: bucket,
          competitorChartSortDirection: 'desc' as const,
        };
        persist(next);
        return next;
      }
      const next = {
        ...state,
        competitorChartSortDirection: state.competitorChartSortDirection === 'desc' ? ('asc' as const) : ('desc' as const),
      };
      persist(next);
      return next;
    }),

  setChartViewMode: (mode) =>
    set((state) => {
      const next = { ...state, chartViewMode: mode };
      persist(next);
      return next;
    }),

  applyFilters: () =>
    set((state) => {
      const next = { ...state, applied: snapshotDraft(state) };
      persist(next);
      return next;
    }),

  discardFilters: () =>
    set((state) => {
      const next = { ...state, ...state.applied };
      persist(next);
      return next;
    }),

  loadPresetState: (snapshot) =>
    set((state) => {
      const audienceSegment = remapAudienceSegmentValues(snapshot.audienceSegment);
      const next = {
        ...state,
        ...snapshot,
        audienceSegment,
        applied: { ...snapshot, audienceSegment },
      };
      persist(next);
      return next;
    }),

  clearDevice: () =>
    set((state) => {
      if (state.device.length === 0 && state.applied.device.length === 0) return state;
      const next = {
        ...state,
        device: [] as string[],
        applied: { ...state.applied, device: [] as string[] },
      };
      persist(next);
      return next;
    }),
}));

export function __resetOverviewFiltersStore(): void {
  try { localStorage.removeItem(STORAGE_KEY); } catch {}
  useOverviewFiltersStore.setState({ ...DEFAULT_STATE, applied: snapshotDraft(DEFAULT_STATE), _lastResetTenantId: null });
}
